import {
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Stack, ThemeProvider } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import { useNavigate } from "react-router-dom";
import useTableTheme from "constants/TableTheme";

/**
 * Client-side MRT table:
 * - Built-in pagination (no external Pagination component)
 * - Client-side sorting/filtering
 * - Optional search box at the top
 * - Sticky header, compact density
 */
function MRTClientDataTable({
    canSearch = true,
    table,
    name,
    isSorted = true,
    enableTopToolbar = false,
    defaultPageSize = 50,
    entriesOption = ["10", "20", "30", "40", "50"],
    navigateRow,
    tableMaxHeight,
    tableMinHeight,
    initialState,
    renderTopToolbarCustomActions,
    renderToolbarInternalActions,
    fullScr,
}) {
    const navigate = useNavigate();
    const tableTheme = useTableTheme();

    const columns = useMemo(
        () =>
            (table?.columns ?? []).map((col) => ({
                accessorKey: col?.accessor ?? col?.accessorKey,
                header: col?.Header,
                size: col?.width,
                muiTableHeadCellProps: {
                    align: col.align ?? "left",
                    sx: { p: 0 },
                },
                muiTableBodyCellProps: {
                    align: col.align ?? "left",
                    sx: {
                        backgroundColor: (row) =>
                            row?.original?.isActive === false ? "#F8F8F8" : undefined,
                        p: 0,
                    },
                },
                Cell: col.Cell ?? undefined,
                // Optional: honor per-column sortable flag (default true if isSorted)
                enableSorting: col?.sortable ?? isSorted,
            })) || [],
        [table?.columns, isSorted]
    );

    const data = useMemo(() => table?.rows ?? [], [table?.rows]);

    const effectiveInitialState = useMemo(() => {
        const base = {
            density: "compact",
            pagination: { pageIndex: 0, pageSize: Number(defaultPageSize) || 50 },
        };
        return initialState ? { ...base, ...initialState } : base;
    }, [initialState, defaultPageSize]);

    const mrt = useMaterialReactTable({
        columns,
        data,
        // CLIENT-SIDE ops
        enablePagination: true,
        manualPagination: false,
        enableSorting: isSorted,
        manualSorting: false,
        enableGlobalFilter: true,
        enableStickyHeader: true,
        enableDensityToggle: false,
        enableTopToolbar,
        renderTopToolbarCustomActions: renderTopToolbarCustomActions,
        renderToolbarInternalActions: renderToolbarInternalActions,

        // Layout / styles
        muiTableContainerProps: {
            sx: {
                boxShadow: "none",
                maxHeight: fullScr ? "calc(100vh - 100px)" : tableMaxHeight || "calc(100vh - 120px)",
                minHeight: fullScr ? "calc(100vh - 100px)" : tableMinHeight || "",
            },
        },
        muiTableBodyRowProps: ({ row }) => ({
            onClick: () => {
                if (navigateRow) navigate(`${navigateRow}/${row.original?.id}`);
            },
            sx: {
                cursor: navigateRow ? "pointer" : "default",
                backgroundColor: row?.original?.isActive === false ? "#F8F8F8" : "",
            },
        }),
        muiTablePaperProps: {
            elevation: 0,
            sx: {
                width: "100%",
                borderRadius: 0,
                backgroundColor: "#fff !important",
                zIndex: 500,
                pl: 0.75,
                paddingLeft: fullScr ?  "0.5rem !important" : "0 !important",
            },
        },
        // rows-per-page dropdown (client-side)
        muiPaginationProps: {
            rowsPerPageOptions: (entriesOption || []).map((n) => Number(n)),
            // keep the control visible even if few rows
            showRowsPerPage: true,
        },
        initialState: effectiveInitialState,
        initialState: {
            showGlobalFilter: true
        },
        muiPaginationProps: {
            shape: 'circular',
            variant: 'outlined'
        },
        paginationDisplayMode: "pages",

        // simple alphanumeric fallback
        sortingFns: {
            alphanumeric: (rowA, rowB, columnId) =>
                String(rowA.getValue(columnId) ?? "").localeCompare(
                    String(rowB.getValue(columnId) ?? "")
                ),
        },
        renderEmptyRowsFallback: () => (
            <Stack
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="14px"
                p={5}
            >
                No records to display
            </Stack>
        ),
    });

    return (
        <>
            {canSearch && (
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={3}
                >
                    <MDBox
                        width="100%"
                        ml="auto"
                        display="flex"
                        alignItems="center"
                        justifyContent={name ? "space-between" : "flex-end"}
                    >
                        {name && <MDBox color="headingColor">{name}</MDBox>}
                        <MDInput
                            width="12rem"
                            placeholder="Search..."
                            size="small"
                            onChange={(e) => mrt.setGlobalFilter(e.target.value)}
                        />
                    </MDBox>
                </MDBox>
            )}

            <ThemeProvider theme={tableTheme}>
                <MaterialReactTable table={mrt} />
            </ThemeProvider>
        </>
    );
}

MRTClientDataTable.propTypes = {
    canSearch: PropTypes.bool,
    table: PropTypes.shape({
        columns: PropTypes.array.isRequired,
        rows: PropTypes.array.isRequired,
    }).isRequired,
    name: PropTypes.string,
    isSorted: PropTypes.bool,
    enableTopToolbar: PropTypes.bool,
    defaultPageSize: PropTypes.number,
    entriesOption: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    navigateRow: PropTypes.string,
    tableMaxHeight: PropTypes.string,
    tableMinHeight: PropTypes.string,
    initialState: PropTypes.object,
};

export default MRTClientDataTable;
